/* A bounded, lazy-loaded product thumbnail rotation inside the desktop Shop panel. */
(() => {
  if (customElements.get('kn-promo-rotation')) return;
  class KnPromoRotation extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.kn-menu-promotion__slide')];
      this.controls = this.querySelector('.kn-menu-promotion__controls');
      if (this.slides.length < 2 || !this.controls) return;
      this.details = this.closest('details');
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.toggle = this.querySelector('.kn-menu-promotion__toggle');
      this.interval = Math.max(3000, Math.min(10000, Number(this.dataset.interval) * 1000 || 4000));
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.generation = (this.generation || 0) + 1;
      this.paused = this.motion.matches;
      this.hovered = false;
      this.loading = new Map();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };

      this.details.addEventListener('toggle', () => this.refresh(), options);
      this.desktop.addEventListener('change', () => this.refresh(), options);
      document.addEventListener('visibilitychange', () => this.refresh(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.refresh();
      }, options);
      this.addEventListener('pointerenter', () => { this.hovered = true; this.refresh(); }, options);
      this.addEventListener('pointerover', (event) => {
        const hovered = !this.controls.contains(event.target);
        if (hovered !== this.hovered) { this.hovered = hovered; this.refresh(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.refresh(); }, options);
      this.addEventListener('focusin', () => this.refresh(), options);
      this.addEventListener('focusout', (event) => this.refresh(event.relatedTarget), options);
      this.toggle.addEventListener('click', () => { this.paused = !this.paused; this.refresh(); }, options);
      this.controls.hidden = false;
      this.refresh();
    }

    disconnectedCallback() {
      this.stop();
      this.listeners?.abort();
    }

    isVisible() {
      return this.isConnected && this.desktop.matches && this.details.open && !document.hidden;
    }

    canRotate(focused = document.activeElement) {
      const controlFocused = this.controls.contains(focused);
      return this.isVisible() && !this.paused && !this.hovered &&
        (controlFocused || !this.contains(focused));
    }

    syncControl() {
      this.toggle.setAttribute('aria-pressed', String(this.paused));
      this.toggle.setAttribute('aria-label', this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
    }

    refresh(focused = document.activeElement) {
      this.syncControl();
      if (!this.canRotate(focused)) {
        this.stop();
        return;
      }
      // Only the current/next image is requested while the panel is visible.
      this.load(this.index);
      this.load((this.index + 1) % this.slides.length);
      if (this.timer || this.advancing) return;
      const generation = this.generation;
      this.timer = setTimeout(async () => {
        this.timer = null;
        this.advancing = true;
        await this.advance(generation);
        this.advancing = false;
        this.refresh();
      }, this.interval);
    }

    stop() {
      clearTimeout(this.timer);
      this.timer = null;
      this.generation = (this.generation || 0) + 1;
    }

    load(index) {
      if (this.loading.has(index)) return this.loading.get(index);
      const img = this.slides[index].querySelector('img');
      img.loading = 'eager';
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        delete img.dataset.srcset;
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
      const ready = img.decode().then(() => img.naturalWidth > 0).catch(() => false);
      this.loading.set(index, ready);
      return ready;
    }

    async advance(generation) {
      if (!this.isVisible()) return;
      for (let step = 1; step < this.slides.length; step++) {
        const next = (this.index + step) % this.slides.length;
        const ready = await this.load(next);
        if (generation !== this.generation || !this.isVisible()) return;
        if (!ready) continue;
        this.slides[this.index].classList.remove('is-active');
        this.slides[this.index].setAttribute('aria-hidden', 'true');
        this.slides[next].classList.add('is-active');
        this.slides[next].setAttribute('aria-hidden', 'false');
        this.index = next;
        break;
      }
    }
  }

  customElements.define('kn-promo-rotation', KnPromoRotation);
})();
